import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Checkbox, Empty, InputNumber } from 'antd'
import { useTranslation } from 'react-i18next'

import { SortableItem } from './SortableItem'

import type { IResolvedColumn } from '../../Helpers/tableSettings'
import type { Dispatch, SetStateAction } from 'react'

interface ColumnsTabProps {
    columns: IResolvedColumn[]
    onChange: Dispatch<SetStateAction<IResolvedColumn[]>>
}

/**
 * Вкладка «Колонки»: DnD-сортування + чекбокс видимості + ширина (px, порожнє = авто).
 */
export const ColumnsTab = ({ columns, onChange }: ColumnsTabProps) => {
    const { t } = useTranslation()

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const oldIndex = columns.findIndex((column) => column.key === active.id)
        const newIndex = columns.findIndex((column) => column.key === over.id)
        if (oldIndex < 0 || newIndex < 0) return

        onChange(arrayMove(columns, oldIndex, newIndex))
    }

    const updateColumn = (key: string, patch: Partial<IResolvedColumn>) => {
        onChange(columns.map((column) => (column.key === key ? { ...column, ...patch } : column)))
    }

    if (columns.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columns.map((column) => column.key)} strategy={verticalListSortingStrategy}>
                {columns.map((column) => (
                    <SortableItem key={column.key} id={column.key}>
                        <Checkbox
                            checked={column.visible}
                            onChange={(e) => updateColumn(column.key, { visible: e.target.checked })}
                        />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {column.label}
                        </span>
                        <InputNumber
                            style={{ width: 110 }}
                            min={0}
                            placeholder={t('auto', { ns: 'Core', defaultValue: 'auto' })}
                            addonAfter="px"
                            value={column.width}
                            onChange={(value) =>
                                updateColumn(column.key, { width: typeof value === 'number' ? value : null })
                            }
                        />
                    </SortableItem>
                ))}
            </SortableContext>
        </DndContext>
    )
}
